'use client';

import { type MouseEvent, useCallback } from 'react';
import { useRouter } from '@/lib/routing';

/** The Navigation API, which TypeScript's DOM lib does not type yet. */
interface WindowWithNavigation extends Window {
  navigation?: { canGoBack: boolean };
}

/**
 * Whether a page of this site is behind the current one. The Navigation API counts
 * same-origin entries only, so a page opened from another site or in a new tab has
 * none; browsers without it fall back to any history at all.
 */
function canGoBackInApp(): boolean {
  const { navigation } = window as WindowWithNavigation;
  return navigation ? navigation.canGoBack : window.history.length > 1;
}

/**
 * Click handler for a Back link: steps back through history when a page of this
 * site is behind, otherwise lets the link follow its own href. A modified click
 * (new tab or window) always follows the href.
 */
export function useHistoryBack() {
  const router = useRouter();
  return useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const isModified =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
      if (isModified || !canGoBackInApp()) return;
      event.preventDefault();
      router.back();
    },
    [router],
  );
}
